import {
  CorpUserContext,
  getAdminPortalUrlWithSource,
  getCurrentCorpAccount,
  getUrlWithUtmParams,
  UserSourceProvider,
  getBusinessEligibleAccounts,
  userContextDefaultValues,
  useUtmParams,
} from "@capone/common";
import {
  ExperimentsHookProvider,
  useExperimentsById,
  useCorpBusinessTabsState,
} from "@capone/experiments";
import * as CorporatePortal from "@capone-corporate/portal";
import { datadogRum } from "@datadog/browser-rum";
import { useDatadogExperimentsContext } from "../utils/datadogUtils";
import {
  StylesProvider,
  ThemeProvider,
  createGenerateClassName,
} from "@material-ui/core/styles";
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Redirect,
  Route,
  RouteComponentProps,
  Switch,
  matchPath,
} from "react-router-dom";
import {
  CallState,
  CorpBusinessAccount,
  CorpSessionInfo,
  CorporateInfo,
  CustomEvents,
  EditTravelerModalState,
  ExperimentStateWithCallState,
  IPerson,
  ModalNames,
  PolicyTier,
  RewardsAccount,
  SessionInfo,
  UserCorpPermissions,
  installColorConfig,
} from "redmond";

import { LoadScript } from "@react-google-maps/api";
import clsx from "clsx";
import {
  Icon,
  IconName,
  TravelerWorkflowDialog,
  useDeviceTypes,
} from "halifax";
import { BasicAuth, JWTAuth, ProtectedRoute } from "../components/Auth";
import AuthModule from "../components/AuthModule";
import AxiosInterceptors from "../components/AxiosInterceptors";
import AppLoadingScreen from "../components/AppLoadingScreen";
import Body from "../components/Body";
import Header from "../components/capone-corporate/Header";
import { RootBanner } from "../components/capone-corporate/RootBanner/component";
import Footer from "../components/Footer";
import { FunnelEntryTabs } from "../components/Header/components/FunnelEntryTabs";
import { HomepageTakeover } from "../components/HomepageTakeover";
import ExperimentsProvider, {
  AVAILABLE,
  CORE_HOMEPAGE,
  CORE_VACATION_RENTALS_SHOP,
  CORP_MYTRIPS_REPLATFORMING,
  getExperimentVariant,
} from "../context/experiments";
import "../fonts.scss";
import { usePathname } from "../hooks/usePathname";
import { useWindowSize } from "../hooks/useWindowSize";
import { useSeenModalUpdater } from "../hooks/useSeenModalUpdater";
import { usePreAuthMaintenance } from "../hooks/usePreAuthMaintenance";
import { xmodColorVars } from "../utils/capone-corporate/colors";
import config, {
  getDateFromUnixString,
} from "../utils/capone-corporate/config";
import {
  isCarsFunnelPath,
  isFlightsFunnelPath,
  isHomepageEntryPath,
  isHotelsFunnelPath,
  isPackagesFunnelPath,
  isPremiumOffersFunnelPath,
  isPremiumStaysFunnelPath,
  isStaysFunnelPath,
  isVacationRentalsShopFunnelPath,
  shouldResolveHomepageDecision,
} from "../utils/homepageEntryPaths";
import {
  CAPONE_HEADER_UPDATED_STYLING_PATHS,
  DISPLAY_HOMEPAGE_TAKEOVER_PATHS,
  HIDDEN_BANNER_UNPROTECTED_PATHS,
  HIDDEN_FOOTER_PATHS,
  HIDDEN_FOOTER_PATHS_MOBILE,
  HIDDEN_FOOTER_UNPROTECTED_PATHS,
  HIDDEN_HEADER_PATHS_DESKTOP,
  HIDDEN_HEADER_PATHS_MOBILE_GLOBAL_NAV,
  HIDDEN_HEADER_UNPROTECTED_PATHS,
  isCoreHomepageBookingConfirmationPath,
  NO_FOOTER_PADDING_PATHS,
  NO_FOOTER_PADDING_UNPROTECTED_PATHS,
  PATH_AUTH,
  PATH_BUSINESS,
  PATH_BUSINESS_REGEX,
  PATH_MY_PROFILE_REGEX,
  PATH_CARS_AVAILABILITY,
  PATH_CARS_SHOP,
  PATH_ACTIVITIES_AVAILABILITY,
  PATH_FLIGHTS_SHOP,
  PATH_HOME,
  PATH_HOTELS_ANCILLARY,
  PATH_HOTELS_ANCILLARY_CUSTOMIZE,
  PATH_HOTELS_AVAILABILITY,
  PATH_HOTELS_SHOP,
  PATH_PACKAGES_HOTEL_AVAILABILITY,
  PATH_PACKAGE_FLIGHT_SHOP,
  PATH_PACKAGE_HOTEL_SHOP,
  PATH_PACKAGE_REVIEW,
  PATH_STAYS_AVAILABILITY,
  PATH_TRIPS,
  UNPROTECTED_PATHS,
  pathsListContainsPath,
} from "../utils/urlPaths";
import "./App.css";
import { corpTheme, corpXmodTheme } from "./theme";

import { pollUntilAllOptedIn } from "../api/v1/multi-account/fetchBusinessAccounts";
import { fetchCanAccessConsumer } from "../api/v1/portal-access/fetchCanAccessConsumer";
import { fetchRewardsAccounts } from "../api/v1/rewards/fetchRewardsAccounts";
import { fetchRewardsAccountsV1 } from "../api/v1/rewards/fetchRewardsAccountsV1";
import { trackEvent } from "../api/v1/trackEvent";
import CorporateLogo from "../assets/b2b/capone-corporate-logo.svg";
import CorporateLoadingLogo from "../assets/b2b/ftux/capone-corp-icon.svg";
import { useActiveModalManager } from "../components/capone-corporate/Header/components/useActiveModalManager";
import { MobileMultiAccountSwitcher } from "../components/capone-corporate/MultiAccount/MobileMultiAccountSwitcher/component";
import { CorpMultiAccountSwitcherModal } from "../components/capone-corporate/MultiAccount/CorpMultiAccountSwitcherModal/component";
import { useSwitchBusinessAccount } from "../components/capone-corporate/MultiAccount/useSwitchBusinessAccount";
import { useShowMultiAccountSwitcherModal } from "../components/capone-corporate/MultiAccount/useShowMultiAccountSwitcherModal";
import { MULTI_ACCOUNT_MODAL_TITLE } from "../components/capone-corporate/Header/components/constants";
import { OnboardingModal } from "../components/capone-corporate/OnboardingModal";
import { SeanEllisSurveyModal } from "../components/capone-corporate/SeanEllisSurveyModal";
import Maintenance from "../components/Maintenance/maintenance";
import { getMaintenanceProps } from "../components/Maintenance/getMaintenanceProps";
import { CAP_ONE_CORP_LOGO_ALT } from "../utils/constants";

import "halifax/build/assets/style.css";
import "@capone-corporate/portal/style.css";
import { fetchBusinessModalMetadata } from "../api/v1/business/fetchBusinessModalMetadata";
import {
  deleteUserPassenger,
  fetchCorpUserPassengers,
  setBusinessAccounts,
  setIsMultiportal,
  setModalMetadataInfoInStore,
  setPolicyTier,
  setRewardsAccountsInStore,
  setSelectedAccountReferenceIdInStore,
  setSessionInfoInStore,
  updateUserPassenger,
} from "../app/actions";
import { getUserPassengers, getUserPassengerCallState } from "../app/selectors";
import { BaseProviders } from "../components/Core";

const generateClassName = createGenerateClassName({
  productionPrefix: "ptBaseModule",
  seed: "ptBaseModule",
});

// TODO: add locale logic by integrating https://www.npmjs.com/package/locale-url-solver
const DEFAULT_LOCALE = "en";

export const StyleContext = createContext({});

type PortalGateStatus = "unknown" | "single-portal" | "multiportal";

const setViewWidthAndHeight = (width: string, height: string) => {
  document.body.style.setProperty(`--vw`, width);
  document.body.style.setProperty(`--vh`, height);
};

const AdminPortalRedirect = ({ targetUrl }: { targetUrl: string }) => {
  React.useLayoutEffect(() => {
    window.location.replace(targetUrl);
  }, [targetUrl]);

  return null;
};

const isDesktopHeaderHidden = (pathname: string) =>
  pathsListContainsPath(HIDDEN_HEADER_PATHS_DESKTOP, pathname) ||
  isHotelsFunnelPath(pathname) ||
  isStaysFunnelPath(pathname) ||
  isPremiumStaysFunnelPath(pathname) ||
  isPremiumOffersFunnelPath(pathname);

const HIDE_REWARDS_BANNER_ON_SCROLL_PATHS = [
  PATH_HOTELS_AVAILABILITY,
  PATH_STAYS_AVAILABILITY,
  PATH_HOTELS_SHOP,
  PATH_HOTELS_ANCILLARY,
  PATH_HOTELS_ANCILLARY_CUSTOMIZE,
  PATH_CARS_AVAILABILITY,
  PATH_CARS_SHOP,
  PATH_FLIGHTS_SHOP,
  PATH_PACKAGES_HOTEL_AVAILABILITY,
  PATH_PACKAGE_HOTEL_SHOP,
  PATH_PACKAGE_FLIGHT_SHOP,
  PATH_PACKAGE_REVIEW,
  PATH_ACTIVITIES_AVAILABILITY,
];

const isInMaintenance = (leamouthConfig: {
  isMaintenanceModeEnabled: string;
  isTimedMaintenanceModeEnabled: string;
  MAINTENANCE_START_TIME?: string;
  MAINTENANCE_END_TIME?: string;
}) => {
  const maintenanceStartTime = getDateFromUnixString(
    leamouthConfig.MAINTENANCE_START_TIME
  );
  const maintenanceEndTime = getDateFromUnixString(
    leamouthConfig.MAINTENANCE_END_TIME
  );

  if (
    leamouthConfig.isTimedMaintenanceModeEnabled === "true" &&
    maintenanceStartTime &&
    maintenanceEndTime
  ) {
    const currentTime = Date.now();

    return (
      currentTime <= maintenanceEndTime && currentTime >= maintenanceStartTime
    );
  }
  return leamouthConfig.isMaintenanceModeEnabled === "true";
};

const CorporatePortalHomepageHeaderV2 = (CorporatePortal as any)
  .CorporatePortalHomepageHeaderV2 as React.ComponentType | undefined;
const CORPORATE_TRAVELER_PROFILE_MENU_CLICK_EVENT =
  "capone-corporate-traveler-profile-menu-click";
const SHOW_TRAVELER_PROFILES_PARAM = "showTravelerProfiles";
const ROOT_ROUTE_REGEX = {
  cars: /^\/cars(?:\/|$)/,
  faq: /^\/faq(?:\/|$)/,
  flights: /^\/flights(?:\/|$)/,
  hotelsOrStays: /^\/(?:hotels|stays)(?:\/|$)/,
  terms: /^\/(terms-of-service|terms|site-terms-of-use)(?:\/|$)/,
  trips: /^\/trips(?:\/|$)/,
  tripsDisruption: /^\/trips\/disruption(?:\/|$)/,
  travelOffers: /^\/travel-offers(?:\/|$)/,
  hotelsFlights: /^\/hotels-flights(?:\/|$)/,
  premiumStaysVibes: /^\/premium-stays\/(vibes)?\/?$/,
  premiumOffers: /^\/premium-offers(?:\/|$)/,
};

const getEditTravelerModalState = (
  pathname: string
): EditTravelerModalState => {
  if (ROOT_ROUTE_REGEX.cars.test(pathname)) {
    return EditTravelerModalState.ground;
  }
  if (ROOT_ROUTE_REGEX.flights.test(pathname)) {
    return EditTravelerModalState.flight;
  }
  if (ROOT_ROUTE_REGEX.hotelsOrStays.test(pathname)) {
    return EditTravelerModalState.hotel;
  }
  if (ROOT_ROUTE_REGEX.trips.test(pathname)) {
    return EditTravelerModalState.trips;
  }
  if (ROOT_ROUTE_REGEX.travelOffers.test(pathname)) {
    return EditTravelerModalState.wallet;
  }
  if (ROOT_ROUTE_REGEX.hotelsFlights.test(pathname)) {
    return EditTravelerModalState.packages;
  }

  return EditTravelerModalState.closed;
};

const App = () => {
  const [sessionInfo, setSessionInfo] = useState<CorpSessionInfo>({
    userId: "",
    userInfo: { firstName: "", lastName: "", email: "" },
    isFirstSession: false,
    csrfToken: "",
    sessionExpiration: "",
    isDelegatedSession: "",
    corporateInfo: {
      businessId: "",
      permissions: {} as UserCorpPermissions,
      businessIsLive: false,
      role: [] as CorporateInfo["role"],
    },
  } as CorpSessionInfo);

  const [rewardAccounts, setRewardAccounts] = useState<RewardsAccount[]>([]);
  const [isRewardsDisabled, setIsRewardsDisabled] = useState(false);
  const [corpBusinessAccounts, setCorpBusinessAccounts] = useState<
    CorpBusinessAccount[]
  >([]);
  const [showMobileMultiAccountSwitcher, setShowMobileMultiAccountSwitcher] =
    useState(false);
  const [selectedRewardsAccountId, setSelectedRewardsAccountId] = useState<
    string | null
  >(null);
  const [isCoreHomepageHeaderScrolled, setIsCoreHomepageHeaderScrolled] =
    useState(false);
  const [consumerAccessStatus, setConsumerAccessStatus] =
    useState<PortalGateStatus>("unknown");
  const [resolvedConsumerAccessKey, setResolvedConsumerAccessKey] =
    useState("");
  const [experimentState, setExperimentState] =
    useState<ExperimentStateWithCallState>();
  useDatadogExperimentsContext(experimentState?.experiments);

  const [isAccountPreselected, setIsAccountPreselected] = useState(false);
  const [policies, setPolicies] = useState<PolicyTier>();
  const [fetchRewardsAccountsCallState, setFetchRewardsAccountsCallState] =
    useState<CallState>(CallState.NotCalled);
  const [launchEventSent, setLaunchEventSent] = useState(false);
  const [isCoreHomepageTravelerModalOpen, setIsCoreHomepageTravelerModalOpen] =
    useState(false);

  const currentAccount = rewardAccounts.find(
    (account) => account.accountReferenceId === selectedRewardsAccountId
  );

  const windowSize = useWindowSize();
  // Add a variable for vh to use for specifying full-screen height
  // 100vh does not work properly on iOS. https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
  setViewWidthAndHeight(
    `${windowSize.width * 0.01}px`,
    `${windowSize.height * 0.01}px`
  );

  const { matchesMobile } = useDeviceTypes();

  useEffect(() => {
    datadogRum.setGlobalContextProperty(
      "channel",
      matchesMobile ? "Mobile" : "Web"
    );
  }, [matchesMobile]);

  const pathname = usePathname();
  const utmParams = useUtmParams();
  const dispatch = useDispatch();
  const travelers = useSelector(getUserPassengers);
  const userPassengerCallState = useSelector(getUserPassengerCallState);

  const { activeModal, closeActiveModal } = useActiveModalManager(
    sessionInfo?.corporateInfo,
    currentAccount,
    corpBusinessAccounts,
    pathname,
    isAccountPreselected
  );

  const { handleChangeBusinessInUse, isAccountSwitchLoading } =
    useSwitchBusinessAccount();
  const {
    setShowMultiAccountsSwitcherModal,
    setHasSeenMultiAccountsSwitcherModal,
  } = useShowMultiAccountSwitcherModal();

  const canViewConsumer = consumerAccessStatus === "multiportal";

  const hasCorpBusinessAccounts =
    corpBusinessAccounts && corpBusinessAccounts.length > 1;

  const handleCloseCorpAccountPickModal = useCallback(() => {
    setHasSeenMultiAccountsSwitcherModal("true");
    setShowMultiAccountsSwitcherModal(false);
    closeActiveModal("multiaccount");
  }, [
    closeActiveModal,
    setHasSeenMultiAccountsSwitcherModal,
    setShowMultiAccountsSwitcherModal,
  ]);

  const closeEditTravelersModal = useCallback(() => {
    setIsCoreHomepageTravelerModalOpen(false);
  }, []);

  const closeSameDayRoundTripModal = useCallback(() => {
    closeActiveModal("sameDayRoundTrip");
  }, [closeActiveModal]);

  const updateSessionInfo = useCallback((sessionInfo: CorpSessionInfo) => {
    setSessionInfo(sessionInfo);
    datadogRum.setUser({ id: sessionInfo.userId });

    dispatch(setSessionInfoInStore(sessionInfo));
  }, []);

  const isStaysPaths = useCallback(
    (location: { pathname: string | string[] }) => {
      const staysPaths = [
        "/stays/availability",
        "/stays/shop",
        "/stays/ancillary",
        "/stays/favorites",
      ];
      return staysPaths.some((path) => location.pathname.includes(path));
    },
    []
  );

  const coreHomepageVariant = getExperimentVariant(
    experimentState?.experiments || [],
    CORE_HOMEPAGE
  );
  const isCoreHomepageExperimentAvailable =
    coreHomepageVariant.startsWith(AVAILABLE);
  const showCoreHomepage =
    consumerAccessStatus === "multiportal" || isCoreHomepageExperimentAvailable;

  const showCorpMyTrips = getExperimentVariant(
    experimentState?.experiments || [],
    CORP_MYTRIPS_REPLATFORMING
  ).startsWith(AVAILABLE);

  const isCoreVacationRentalsShopActive = getExperimentVariant(
    experimentState?.experiments || [],
    CORE_VACATION_RENTALS_SHOP
  ).startsWith(AVAILABLE);

  const { enabled: businessTabsEnabled, isResolved: businessTabsResolved } =
    useCorpBusinessTabsState();

  const isCoreHomepageEntryPath = (pathname: string) =>
    showCoreHomepage && isHomepageEntryPath(pathname);
  const isCoreHomepageHeaderPath = (pathname: string) =>
    showCoreHomepage &&
    !ROOT_ROUTE_REGEX.tripsDisruption.test(pathname) &&
    (ROOT_ROUTE_REGEX.faq.test(pathname) ||
      ROOT_ROUTE_REGEX.terms.test(pathname) ||
      ROOT_ROUTE_REGEX.travelOffers.test(pathname) ||
      (!showCorpMyTrips && ROOT_ROUTE_REGEX.trips.test(pathname)) ||
      ROOT_ROUTE_REGEX.premiumStaysVibes.test(pathname) ||
      ROOT_ROUTE_REGEX.premiumOffers.test(pathname));
  const isCoreHomepageBookingConfirmationHeaderPath = (pathname: string) =>
    showCoreHomepage && isCoreHomepageBookingConfirmationPath(pathname);
  const usesCoreHomepageHeader = (pathname: string) =>
    isCoreHomepageHeaderPath(pathname) ||
    isCoreHomepageBookingConfirmationHeaderPath(pathname);
  const shouldRenderCoreHomepageOverlay = (pathname: string) =>
    isCoreHomepageEntryPath(pathname) || isCoreHomepageHeaderPath(pathname);
  const shouldFetchHomepageDecision = shouldResolveHomepageDecision(pathname);
  const consumerAccessResolutionKey =
    shouldFetchHomepageDecision && sessionInfo.csrfToken
      ? sessionInfo.csrfToken
      : "";
  const isConsumerAccessResolved =
    !consumerAccessResolutionKey ||
    resolvedConsumerAccessKey === consumerAccessResolutionKey;
  const isHomepageExperimentResolved =
    !shouldFetchHomepageDecision ||
    !sessionInfo.csrfToken ||
    experimentState?.callState === CallState.Success;
  const isHomepageDecisionResolved =
    !shouldFetchHomepageDecision ||
    !sessionInfo.csrfToken ||
    (isHomepageExperimentResolved && isConsumerAccessResolved);

  const getCoreHomepageHeaderElement = (routePathname: string) => {
    if (
      !usesCoreHomepageHeader(routePathname) ||
      !CorporatePortalHomepageHeaderV2
    ) {
      return null;
    }

    return <CorporatePortalHomepageHeaderV2 />;
  };

  const isProtectedHeaderHidden = (routePathname: string) =>
    matchesMobile
      ? pathsListContainsPath(
          HIDDEN_HEADER_PATHS_MOBILE_GLOBAL_NAV,
          routePathname
        ) ||
        (showCoreHomepage &&
          (isHotelsFunnelPath(routePathname) ||
            isStaysFunnelPath(routePathname) ||
            isCarsFunnelPath(routePathname) ||
            isFlightsFunnelPath(routePathname) ||
            isPackagesFunnelPath(routePathname) ||
            isPremiumStaysFunnelPath(routePathname))) ||
        (showCorpMyTrips && routePathname.startsWith(PATH_TRIPS))
      : isDesktopHeaderHidden(routePathname) ||
        (showCorpMyTrips && routePathname.startsWith(PATH_TRIPS));

  const getUnprotectedAppContentClassName = (routePathname: string) =>
    clsx("App-content", {
      "no-padding": NO_FOOTER_PADDING_UNPROTECTED_PATHS.includes(routePathname),
      "core-homepage-header": usesCoreHomepageHeader(routePathname),
      "core-homepage-header-scrolled":
        usesCoreHomepageHeader(routePathname) && isCoreHomepageHeaderScrolled,
      mobile: matchesMobile,
    });

  const getUnprotectedBodyClassName = (routePathname: string) =>
    clsx({
      "desktop-top-margin":
        !matchesMobile &&
        !usesCoreHomepageHeader(routePathname) &&
        !matchPath(routePathname, HIDDEN_HEADER_UNPROTECTED_PATHS),
    });

  const renderLegacyHeader = (
    routePathname: string,
    notAuthenticated = false,
    className = ""
  ) => (
    <Header
      className={className}
      canViewConsumer={canViewConsumer}
      locationPath={routePathname}
      language={DEFAULT_LOCALE}
      rewardsAccounts={rewardAccounts}
      selectedRewardsAccountId={selectedRewardsAccountId}
      setSelectedRewardsAccountId={setSelectedRewardsAccountId}
      notAuthenticated={notAuthenticated}
      corpBusinessAccounts={corpBusinessAccounts}
    />
  );

  const renderUnprotectedHeader = (
    routePathname: string,
    notAuthenticated: boolean
  ) => {
    if (HIDDEN_HEADER_UNPROTECTED_PATHS.includes(routePathname)) {
      return null;
    }

    return (
      getCoreHomepageHeaderElement(routePathname) ||
      renderLegacyHeader(routePathname, notAuthenticated)
    );
  };

  const defaultTravelerId = sessionInfo?.corporateInfo?.defaultTravelerId ?? "";
  const userRoles = sessionInfo?.corporateInfo?.role ?? [];

  const handleTravelerProfileMenuClick = useCallback(() => {
    const currentPathname = window.location.pathname;
    const modalState = getEditTravelerModalState(currentPathname);
    const routeModuleHandlesModal =
      modalState !== EditTravelerModalState.closed && !showCoreHomepage;
    if (routeModuleHandlesModal) {
      document.dispatchEvent(
        new CustomEvent(CustomEvents.editTravelersStateChange, {
          detail: modalState,
        })
      );
    } else {
      setIsCoreHomepageTravelerModalOpen(true);
    }
  }, [showCoreHomepage]);

  useEffect(() => {
    window.addEventListener(
      CORPORATE_TRAVELER_PROFILE_MENU_CLICK_EVENT,
      handleTravelerProfileMenuClick
    );

    return () => {
      window.removeEventListener(
        CORPORATE_TRAVELER_PROFILE_MENU_CLICK_EVENT,
        handleTravelerProfileMenuClick
      );
    };
  }, [handleTravelerProfileMenuClick]);

  const [waitingForTravelerProfiles, setWaitingForTravelerProfiles] = useState(
    () =>
      new URLSearchParams(window.location.search).get(
        SHOW_TRAVELER_PROFILES_PARAM
      ) === "true"
  );

  useEffect(() => {
    if (!waitingForTravelerProfiles) return;

    if (userPassengerCallState === CallState.Failed) {
      setWaitingForTravelerProfiles(false);
      return;
    }

    if (userPassengerCallState === CallState.Success) {
      const params = new URLSearchParams(window.location.search);
      params.delete(SHOW_TRAVELER_PROFILES_PARAM);
      const remainingSearch = params.toString();
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}${
          remainingSearch ? `?${remainingSearch}` : ""
        }`
      );
      setWaitingForTravelerProfiles(false);
      handleTravelerProfileMenuClick();
    }
  }, [
    waitingForTravelerProfiles,
    userPassengerCallState,
    handleTravelerProfileMenuClick,
  ]);

  useEffect(() => {
    if (rewardAccounts.length > 0) {
      dispatch(fetchCorpUserPassengers());
    }
  }, [rewardAccounts, dispatch]);

  const getAdminRedirectUrl = useCallback(
    (pathname: string) => {
      const adminPortalBaseUrl =
        window.__portal_env__?.ADMIN_PORTAL_URL ??
        process.env?.ADMIN_PORTAL_URL;

      if (!adminPortalBaseUrl) {
        return undefined;
      }

      const adminPathSuffix = pathname.replace(/^\/admin\/?/, "/");
      const adminPath = adminPathSuffix === "/" ? undefined : adminPathSuffix;

      return getUrlWithUtmParams(
        getAdminPortalUrlWithSource(adminPath),
        utmParams
      );
    },
    [utmParams]
  );

  const userContext = useMemo(
    () => ({
      ...userContextDefaultValues,
      sessionInfo,
      updateSessionInfo,
      policies,
      setPolicies,
      logo: {
        src: CorporateLogo,
        alt: CAP_ONE_CORP_LOGO_ALT,
      },
      isMultiAccount: corpBusinessAccounts.length !== 1,
      isMultiportal: canViewConsumer,
      modalManagerProps: {
        activeModal,
        closeActiveModal,
      },
      isRewardsDisabled,
    }),
    [
      policies,
      sessionInfo,
      updateSessionInfo,
      corpBusinessAccounts.length,
      canViewConsumer,
      activeModal,
      closeActiveModal,
      isRewardsDisabled,
    ]
  );

  const handleSeenNfuInfoModal = useSeenModalUpdater(
    sessionInfo,
    setSessionInfo as React.Dispatch<
      React.SetStateAction<SessionInfo | CorpSessionInfo | undefined>
    >
  );

  const hasSeenNfuInfoModal =
    sessionInfo?.corporateInfo?.hasSeenModalMap?.[
      ModalNames.NFU_IN_PRODUCT_AWARENESS_PROFILES
    ] ?? false;

  useEffect(() => {
    if (policies) {
      dispatch(setPolicyTier(policies));
    }
  }, [policies]);

  useEffect(() => {
    if (!launchEventSent && sessionInfo?.csrfToken) {
      setFetchRewardsAccountsCallState(CallState.InProcess);
      pollUntilAllOptedIn().then((corpBusinessAccounts) => {
        const filteredAccounts = corpBusinessAccounts.filter((account) => {
          if (!account.optedIn) {
            return true;
          }
          return account.optedIn.permissions.canBookTravel;
        });
        setShowMobileMultiAccountSwitcher(
          filteredAccounts && filteredAccounts.length > 1
        );
        setCorpBusinessAccounts(filteredAccounts);
        dispatch(setBusinessAccounts(filteredAccounts));
      });
      fetchRewardsAccounts()
        .then((rewardsAccounts) => {
          setFetchRewardsAccountsCallState(CallState.Success);
          const businessEligibleAccounts = getBusinessEligibleAccounts(
            rewardsAccounts,
            sessionInfo
          );
          setRewardAccounts(businessEligibleAccounts);
          dispatch(setRewardsAccountsInStore(businessEligibleAccounts));
        })
        .catch(() => setFetchRewardsAccountsCallState(CallState.Failed));
    }
  }, [launchEventSent, sessionInfo?.csrfToken]);

  useEffect(() => {
    if (!launchEventSent && sessionInfo?.csrfToken) {
      fetchRewardsAccountsV1()
        .then(({ isRewardsDisabled: rewardsDisabled }) => {
          setIsRewardsDisabled(rewardsDisabled);
        })
        .catch(() => {
          // isRewardsDisabled is banner-only styling; leave the last known value on failure.
        });
    }
  }, [launchEventSent, sessionInfo?.csrfToken]);

  useEffect(() => {
    if (sessionInfo.csrfToken) {
      fetchBusinessModalMetadata()
        .then((businessModalMetadata) => {
          dispatch(
            setModalMetadataInfoInStore(
              businessModalMetadata.hasSeenModalMap,
              businessModalMetadata.modalDataMap
            )
          );
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }, [sessionInfo.csrfToken]);

  useEffect(() => {
    if (!consumerAccessResolutionKey) {
      setResolvedConsumerAccessKey(consumerAccessResolutionKey);
      return;
    }

    fetchCanAccessConsumer()
      .then((canAccessConsumer) => {
        setConsumerAccessStatus(
          canAccessConsumer ? "multiportal" : "single-portal"
        );
        dispatch(setIsMultiportal(canAccessConsumer));
      })
      .catch(() => {
        setConsumerAccessStatus("unknown");
        dispatch(setIsMultiportal(false));
      })
      .finally(() => setResolvedConsumerAccessKey(consumerAccessResolutionKey));
  }, [consumerAccessResolutionKey, dispatch]);

  useEffect(() => {
    if (!rewardAccounts.length) return;

    const id =
      getCurrentCorpAccount(rewardAccounts, sessionInfo)?.accountReferenceId ||
      rewardAccounts[0]?.accountReferenceId ||
      "";

    setSelectedRewardsAccountId(id);
    dispatch(setSelectedAccountReferenceIdInStore(id));
    localStorage.setItem("referenceId", id ?? "");
  }, [rewardAccounts, sessionInfo, dispatch]);

  useEffect(() => {
    installColorConfig(xmodColorVars);
    document.body.classList.add("xmod");
  }, []);

  useEffect(() => {
    const preloadImg = new Image();
    preloadImg.src = CorporateLoadingLogo;
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setIsCoreHomepageHeaderScrolled(window.scrollY > 0);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const maintenanceModeConfig = useExperimentsById("corp-traveler-maintenance");

  const preAuthMaintenance = usePreAuthMaintenance();

  if (
    isInMaintenance(config) ||
    maintenanceModeConfig?.variant === "page" ||
    preAuthMaintenance
  ) {
    const maintenanceProps = getMaintenanceProps(
      preAuthMaintenance,
      maintenanceModeConfig?.data
    );
    return (
      <Maintenance
        title={maintenanceProps.title}
        subtext={maintenanceProps.subtext}
        icon={maintenanceProps.icon}
      />
    );
  }

  return (
    <ExperimentsProvider
      isLoggedIn={!!sessionInfo.csrfToken}
      onStateChange={setExperimentState}
    >
      <ExperimentsHookProvider isLoggedIn={!!sessionInfo.csrfToken}>
        <LoadScript googleMapsApiKey={config.googleMapsApiKey || ""}>
          <BaseProviders showCoreHomepage={showCoreHomepage}>
            <UserSourceProvider>
              <AxiosInterceptors />
              <StyleContext.Provider value={{ corpTheme }}>
                <StylesProvider generateClassName={generateClassName}>
                  <ThemeProvider theme={corpXmodTheme?.theme}>
                    <JWTAuth>
                      <CorpUserContext.Provider value={userContext}>
                        <div
                          className={clsx("App-container", {
                            "core-homepage-mobile":
                              showCoreHomepage && matchesMobile,
                          })}
                        >
                          <BasicAuth redirectUrl={PATH_HOME} />
                          <Switch>
                            {stripPathLeaf()}
                            <Route
                              path={PATH_AUTH}
                              render={(
                                browserRouterProps: RouteComponentProps
                              ) => (
                                <AuthModule
                                  language={DEFAULT_LOCALE}
                                  {...browserRouterProps}
                                />
                              )}
                            />
                            <Route path="*">
                              <ProtectedRoute
                                protectedUrl={PATH_HOME}
                                onAccountPreselected={() =>
                                  setIsAccountPreselected(true)
                                }
                                isAccountPreselected={isAccountPreselected}
                                // eslint-disable-next-line react/no-unstable-nested-components
                                protectedContent={(pathname: string) => {
                                  const isHomepageDecisionPending =
                                    !!sessionInfo.csrfToken &&
                                    shouldFetchHomepageDecision &&
                                    !isHomepageDecisionResolved;
                                  const shouldBlockOnHomepageDecision =
                                    isHomepageDecisionPending &&
                                    !isCoreHomepageBookingConfirmationPath(
                                      pathname
                                    );

                                  const isBusinessTabsPending =
                                    !!sessionInfo.csrfToken &&
                                    !businessTabsResolved &&
                                    pathname.startsWith("/admin");

                                  if (
                                    shouldBlockOnHomepageDecision ||
                                    isBusinessTabsPending ||
                                    waitingForTravelerProfiles
                                  ) {
                                    return (
                                      <AppLoadingScreen
                                        logoSrc={CorporateLoadingLogo}
                                        logoAlt={CAP_ONE_CORP_LOGO_ALT}
                                      />
                                    );
                                  }

                                  if (
                                    businessTabsEnabled &&
                                    pathname.startsWith("/admin")
                                  ) {
                                    const suffix = pathname.replace(
                                      /^\/admin\/?/,
                                      ""
                                    );
                                    return (
                                      <Redirect
                                        to={`${PATH_BUSINESS}${suffix}${window.location.search}`}
                                      />
                                    );
                                  }

                                  if (
                                    showCoreHomepage &&
                                    pathname.startsWith("/admin")
                                  ) {
                                    const adminUrl =
                                      getAdminRedirectUrl(pathname);
                                    if (!adminUrl) {
                                      return <Redirect to={PATH_BUSINESS} />;
                                    }

                                    return (
                                      <AdminPortalRedirect
                                        targetUrl={adminUrl}
                                      />
                                    );
                                  }

                                  return (
                                    <>
                                      <div
                                        className={clsx("App-content", "corp", {
                                          // TODO: verify on all URL paths (that do not render Footer) to see if any of
                                          // them should be included in NO_FOOTER_PADDING_PATHS
                                          "no-padding":
                                            NO_FOOTER_PADDING_PATHS.includes(
                                              pathname
                                            ),
                                          homepage:
                                            DISPLAY_HOMEPAGE_TAKEOVER_PATHS.includes(
                                              pathname
                                            ),
                                          mobile: matchesMobile,
                                          "core-homepage-header":
                                            usesCoreHomepageHeader(pathname),
                                          "core-homepage-header-scrolled":
                                            usesCoreHomepageHeader(pathname) &&
                                            isCoreHomepageHeaderScrolled,
                                        })}
                                      >
                                        <Route
                                          render={(props) => {
                                            const shouldHideOnScroll =
                                              matchesMobile &&
                                              HIDE_REWARDS_BANNER_ON_SCROLL_PATHS.includes(
                                                props.location.pathname
                                              );
                                            const isCoreHomepageBookingConfirmation =
                                              isCoreHomepageBookingConfirmationHeaderPath(
                                                props.location.pathname
                                              );
                                            const coreHomepageHeader =
                                              getCoreHomepageHeaderElement(
                                                props.location.pathname
                                              );
                                            const rootBanner =
                                              !pathsListContainsPath(
                                                [
                                                  PATH_BUSINESS_REGEX,
                                                  PATH_MY_PROFILE_REGEX,
                                                ],
                                                pathname
                                              ) &&
                                              !(
                                                showCorpMyTrips &&
                                                props.location.pathname.startsWith(
                                                  PATH_TRIPS
                                                )
                                              ) ? (
                                                <RootBanner
                                                  canViewConsumer={
                                                    canViewConsumer
                                                  }
                                                  shouldShowCoreHomepageHeader={
                                                    showCoreHomepage
                                                  }
                                                  currentAccount={
                                                    currentAccount
                                                  }
                                                  isRewardsDisabled={
                                                    isRewardsDisabled
                                                  }
                                                  {...props}
                                                  hideOnScroll={
                                                    shouldHideOnScroll
                                                  }
                                                />
                                              ) : null;

                                            if (
                                              isCoreHomepageEntryPath(
                                                props.location.pathname
                                              )
                                            ) {
                                              return null;
                                            }
                                            if (
                                              coreHomepageHeader &&
                                              !isCoreHomepageBookingConfirmation
                                            ) {
                                              return coreHomepageHeader;
                                            }
                                            if (
                                              coreHomepageHeader &&
                                              isCoreHomepageBookingConfirmation
                                            ) {
                                              return (
                                                <>
                                                  {rootBanner}
                                                  <div className="core-homepage-confirmation-header-offset">
                                                    {coreHomepageHeader}
                                                  </div>
                                                </>
                                              );
                                            }
                                            const isHeaderHidden =
                                              isProtectedHeaderHidden(
                                                props.location.pathname
                                              );

                                            const showMobileHomepageHeader =
                                              isHeaderHidden &&
                                              matchesMobile &&
                                              (isPackagesFunnelPath(
                                                props.location.pathname
                                              ) ||
                                                (isPremiumStaysFunnelPath(
                                                  props.location.pathname
                                                ) &&
                                                  !(
                                                    isCoreVacationRentalsShopActive &&
                                                    isVacationRentalsShopFunnelPath(
                                                      props.location.pathname
                                                    )
                                                  ))) &&
                                              CorporatePortalHomepageHeaderV2;

                                            return (
                                              <>
                                                {rootBanner}
                                                {showMobileHomepageHeader ? (
                                                  <div className="core-homepage-mobile-funnel-header">
                                                    <CorporatePortalHomepageHeaderV2 />
                                                  </div>
                                                ) : null}
                                                {!isHeaderHidden &&
                                                  renderLegacyHeader(
                                                    pathname,
                                                    false,
                                                    !CAPONE_HEADER_UPDATED_STYLING_PATHS.includes(
                                                      props.location.pathname
                                                    ) &&
                                                      !isStaysPaths(
                                                        props.location
                                                      )
                                                      ? "homepage"
                                                      : ""
                                                  )}
                                              </>
                                            );
                                          }}
                                        />
                                        <Route
                                          render={(props) => {
                                            if (
                                              !shouldRenderCoreHomepageOverlay(
                                                props.location.pathname
                                              )
                                            ) {
                                              return null;
                                            }

                                            return policies && sessionInfo ? (
                                              <OnboardingModal
                                                policies={policies}
                                                currentAccount={currentAccount}
                                              />
                                            ) : null;
                                          }}
                                        />
                                        <Route
                                          render={(props) => {
                                            if (
                                              !shouldRenderCoreHomepageOverlay(
                                                props.location.pathname
                                              )
                                            ) {
                                              return null;
                                            }

                                            return (
                                              <SeanEllisSurveyModal
                                                isOpen={
                                                  activeModal === "seanEllis"
                                                }
                                                onClose={() =>
                                                  closeActiveModal("seanEllis")
                                                }
                                              />
                                            );
                                          }}
                                        />
                                        <TravelerWorkflowDialog
                                          tenant={config.TENANT}
                                          showHotelLoyaltySection
                                          showGroundLoyaltySection
                                          disableDriversLicenseValidation
                                          disallowSelect
                                          displayModalSubtitle
                                          onAddPass={() => {}}
                                          showAdditionalInfoSection
                                          showDriverLicenseSection
                                          showFrequentFlyerSection
                                          showGenderField
                                          showNationalityField
                                          buttonClassName="b2b"
                                          dialogProps={{
                                            className: "cars-edit-traveler",
                                            onBackdropClick:
                                              closeEditTravelersModal,
                                            onClose: closeEditTravelersModal,
                                            open: isCoreHomepageTravelerModalOpen,
                                          }}
                                          errorMessage="Add or choose one traveler to continue."
                                          fullScreenWithBanner={matchesMobile}
                                          handleDeletePassenger={(
                                            personId: string
                                          ) => {
                                            dispatch(
                                              deleteUserPassenger({ personId })
                                            );
                                          }}
                                          handleUpdatePassenger={(
                                            person: IPerson,
                                            hideProfileAction,
                                            entryPoint
                                          ) => {
                                            dispatch(
                                              updateUserPassenger(
                                                { person, hideProfileAction },
                                                defaultTravelerId,
                                                entryPoint
                                              )
                                            );
                                          }}
                                          isMobile={matchesMobile}
                                          travelers={travelers}
                                          titles={{}}
                                          dialogContentProps={{}}
                                          userRoles={userRoles}
                                          trackEvent={trackEvent}
                                          defaultTravelerId={defaultTravelerId}
                                          hasSeenNfuInfo={hasSeenNfuInfoModal}
                                          handleSeenNfuInfoModal={
                                            handleSeenNfuInfoModal
                                          }
                                        />
                                        <Route
                                          render={(props) => {
                                            if (
                                              !DISPLAY_HOMEPAGE_TAKEOVER_PATHS.includes(
                                                props.location.pathname
                                              ) ||
                                              isCoreHomepageEntryPath(
                                                props.location.pathname
                                              )
                                            ) {
                                              return null;
                                            }
                                            if (matchesMobile) {
                                              return (
                                                <>
                                                  {showMobileMultiAccountSwitcher &&
                                                    !showCoreHomepage && (
                                                      <MobileMultiAccountSwitcher
                                                        accounts={
                                                          corpBusinessAccounts
                                                        }
                                                        sessionInfo={
                                                          sessionInfo
                                                        }
                                                      />
                                                    )}
                                                  <HomepageTakeover
                                                    {...props}
                                                    rewardsAccounts={
                                                      rewardAccounts
                                                    }
                                                  />
                                                  <FunnelEntryTabs
                                                    {...props}
                                                    isMobile
                                                  />
                                                </>
                                              );
                                            }
                                            return (
                                              <HomepageTakeover
                                                {...props}
                                                rewardsAccounts={rewardAccounts}
                                              />
                                            );
                                          }}
                                        />
                                        <Body
                                          language={DEFAULT_LOCALE}
                                          isFirstSession={
                                            sessionInfo.isFirstSession
                                          }
                                          launchEventSent={launchEventSent}
                                          setLaunchEventSent={
                                            setLaunchEventSent
                                          }
                                          className={clsx({
                                            "desktop-top-margin":
                                              !isCoreHomepageEntryPath(
                                                pathname
                                              ) &&
                                              !usesCoreHomepageHeader(
                                                pathname
                                              ) &&
                                              !isDesktopHeaderHidden(
                                                pathname
                                              ) &&
                                              !(
                                                showCorpMyTrips &&
                                                pathname.startsWith(PATH_TRIPS)
                                              ) &&
                                              !matchesMobile,
                                          })}
                                          rewardsAccounts={rewardAccounts}
                                          fetchRewardsAccountsCallState={
                                            fetchRewardsAccountsCallState
                                          }
                                          selectedRewardsAccountId={
                                            selectedRewardsAccountId
                                          }
                                          pathname={pathname}
                                          showCoreHomepage={showCoreHomepage}
                                          coreHomepageVariant={
                                            coreHomepageVariant
                                          }
                                          consumerAccessStatus={
                                            consumerAccessStatus
                                          }
                                          isSameDayRoundTripModalOpen={
                                            activeModal === "sameDayRoundTrip"
                                          }
                                          onSameDayRoundTripModalClose={
                                            closeSameDayRoundTripModal
                                          }
                                        />
                                      </div>
                                      <Route
                                        render={(props) => {
                                          if (
                                            isCoreHomepageEntryPath(
                                              props.location.pathname
                                            ) ||
                                            HIDDEN_FOOTER_PATHS.includes(
                                              props.location.pathname
                                            ) ||
                                            (matchesMobile &&
                                              HIDDEN_FOOTER_PATHS_MOBILE.includes(
                                                props.location.pathname
                                              ))
                                          ) {
                                            return null;
                                          }
                                          return (
                                            <Footer
                                              pathname={props.location.pathname}
                                            />
                                          );
                                        }}
                                      />
                                      {hasCorpBusinessAccounts &&
                                        isHomepageEntryPath(pathname) && (
                                          <CorpMultiAccountSwitcherModal
                                            open={
                                              activeModal === "multiaccount"
                                            }
                                            onClose={
                                              handleCloseCorpAccountPickModal
                                            }
                                            headerImage={
                                              <Icon
                                                name={
                                                  IconName.MultiAccountCards
                                                }
                                              />
                                            }
                                            title={MULTI_ACCOUNT_MODAL_TITLE}
                                            accounts={corpBusinessAccounts}
                                            sessionInfo={sessionInfo}
                                            handleChangeBusinessInUse={
                                              handleChangeBusinessInUse
                                            }
                                            isAccountSwitchLoading={
                                              isAccountSwitchLoading
                                            }
                                          />
                                        )}
                                    </>
                                  );
                                }}
                                // eslint-disable-next-line react/no-unstable-nested-components
                                unprotectedContent={(
                                  notAuthenticated: boolean
                                ) => (
                                  <Route
                                    path={UNPROTECTED_PATHS}
                                    render={(prop) => (
                                      <>
                                        <div
                                          className={getUnprotectedAppContentClassName(
                                            prop.location.pathname
                                          )}
                                        >
                                          <RootBanner
                                            canViewConsumer={canViewConsumer}
                                            shouldShowCoreHomepageHeader={
                                              showCoreHomepage
                                            }
                                            isHidden={
                                              HIDDEN_BANNER_UNPROTECTED_PATHS.includes(
                                                prop.location.pathname
                                              ) || notAuthenticated
                                            }
                                            currentAccount={currentAccount}
                                            isRewardsDisabled={
                                              isRewardsDisabled
                                            }
                                            {...prop}
                                          />
                                          {renderUnprotectedHeader(
                                            prop.location.pathname,
                                            notAuthenticated
                                          )}
                                          <Body
                                            language={DEFAULT_LOCALE}
                                            isFirstSession={
                                              sessionInfo.isFirstSession
                                            }
                                            launchEventSent={launchEventSent}
                                            setLaunchEventSent={
                                              setLaunchEventSent
                                            }
                                            className={getUnprotectedBodyClassName(
                                              prop.location.pathname
                                            )}
                                            rewardsAccounts={rewardAccounts}
                                            selectedRewardsAccountId={
                                              selectedRewardsAccountId
                                            }
                                            fetchRewardsAccountsCallState={
                                              fetchRewardsAccountsCallState
                                            }
                                            pathname={prop.location.pathname}
                                            notAuthenticated={notAuthenticated}
                                            showCoreHomepage={showCoreHomepage}
                                            coreHomepageVariant={
                                              coreHomepageVariant
                                            }
                                            consumerAccessStatus={
                                              consumerAccessStatus
                                            }
                                          />
                                        </div>
                                        {!(
                                          isCoreHomepageEntryPath(
                                            prop.location.pathname
                                          ) ||
                                          HIDDEN_FOOTER_UNPROTECTED_PATHS.includes(
                                            prop.location.pathname
                                          )
                                        ) && <Footer />}
                                      </>
                                    )}
                                  />
                                )}
                              />
                            </Route>
                          </Switch>
                        </div>
                      </CorpUserContext.Provider>
                    </JWTAuth>
                  </ThemeProvider>
                </StylesProvider>
              </StyleContext.Provider>
            </UserSourceProvider>
          </BaseProviders>
        </LoadScript>
      </ExperimentsHookProvider>
    </ExperimentsProvider>
  );
};

export default App;

const stripPathLeaf = () =>
  (
    <>
      {/* note: standardize the path to have EXACTLY ONE trailing slash WITHOUT index.html suffix */}
      <Route
        exact
        strict
        path="/:url*//*"
        render={(props: RouteComponentProps) => (
          <Redirect
            to={{
              ...props.location,
              pathname: `${props.location.pathname.replace(/\/+$/, "")}/`,
            }}
          />
        )}
      />
      <Route
        exact
        path="/:url*/index.html/"
        render={(props: RouteComponentProps) => (
          <Redirect
            to={{
              ...props.location,
              pathname: props.location.pathname.slice(0, -"index.html/".length),
            }}
          />
        )}
      />
      <Route
        exact
        path="/:url*/index.html"
        render={(props: RouteComponentProps) => (
          <Redirect
            to={{
              ...props.location,
              pathname: props.location.pathname.slice(0, -"index.html".length),
            }}
          />
        )}
      />
      <Route
        exact
        strict
        path="/:url*"
        render={(props: RouteComponentProps) => (
          <Redirect
            to={{
              ...props.location,
              pathname: `${props.location.pathname}/`,
            }}
          />
        )}
      />
    </>
  ).props.children;
// a solution so that Switch respects Route's from a Fragment; see https://github.com/ReactTraining/react-router/issues/5785
